import { state, setState, createEmptyRoute } from '../lib/state.js';
import { initMap, fitRoute } from '../ui/map.js';
import { initLineManager, refreshLineManager } from '../services/line-manager.js';
import { onServerCalculatorMessage } from '../services/server-calculator-bridge.js';
import { onBrowserlessCalculatorMessage } from '../services/browserless-calculator-bridge.js';
import { onCountryBrowserlessMessage } from '../services/browserless-country-dispatch.js';
import { initBrowserlessMonthlyUi } from '../services/browserless-monthly-ui.js';
import { initEtollWeeklyUi } from '../services/etoll-weekly-ui.js';
import { initAutomationNotifications } from '../services/automation-notifications.js';
import { initInactivityLogout } from '../services/inactivity-logout.js';
import { supabase } from '../lib/supabase.js';
import { els } from './dom.js';
import { RENDER_EVENT, requestRender } from './events.js';
import { initialize, configureAuth, authAction, applySession, handleAutomaticLogout } from './auth.js';
import { showWorkspace, setTopNavigation } from './navigation.js';
import { renderAll, updateButtons } from './renderer.js';
import { importPdf, resolvePoints, analyzeRoute, saveRoute, newRoute, syncHeaderFields } from '../routes/route-actions.js';
import { exportCurrentReport } from '../routes/report-actions.js';
import { openMapPreview, closeMapPreview, closeBorderEditor, startBorderPointPick, saveBorderEdit, syncBorderMarkerFromInputs, enterPointsEditMode, reverseEditableRoute, addEditablePoint } from '../routes/route-editor.js';
import { pointsForCurrentView } from '../routes/route-plan.js';
import { setRoutesCarrierFilter } from '../routes/route-list.js';
import { renderRoutes } from '../routes/route-list.js';
import { openMytoDialog, closeMytoDialog, submitMytoDialog, refreshCurrentMytoBorders } from '../calculators/myto-cz.js';
import { closeAsfinagDialog, submitAsfinagDialog } from '../calculators/asfinag-at.js';
import { closeHugoDialog, submitHugoDialog } from '../calculators/hugo-hu.js';
import { runAllCalculatorsForCurrentLine } from '../calculators/orchestrator.js';
import { handleCalculatorMessage } from '../calculators/result-handler.js';
import { setStatus, setBadge } from '../lib/app-helpers.js';

let protectedAppStarted = false;

function startProtectedApp() {
  if (protectedAppStarted) return;
  protectedAppStarted = true;

  initMap();
  initLineManager({
    getOrganizationId: () => state.organizationId,
    onLineDeleted: ({ routeIds = [] } = {}) => {
      const deleted = new Set(routeIds);
      state.routes = (state.routes || []).filter(route => !deleted.has(route.id));
      if (state.currentRoute?.id && deleted.has(state.currentRoute.id)) {
        setState({ currentRoute:createEmptyRoute() });
        if (els.pdfFile) els.pdfFile.value = '';
      }
      requestRender();
    },
  });
  onServerCalculatorMessage(handleCalculatorMessage);
  onBrowserlessCalculatorMessage(handleCalculatorMessage);
  onCountryBrowserlessMessage(handleCalculatorMessage);
  initBrowserlessMonthlyUi();
  initEtollWeeklyUi();
  initAutomationNotifications();
  initInactivityLogout({
    supabase,
    onLoggedOut: handleAutomaticLogout,
  });
}

function bindEvents() {
  els.loginForm?.addEventListener('submit', event => {
    event.preventDefault();
    authAction();
  });
  els.signOutBtn?.addEventListener('click', async event => {
    event.preventDefault();

    const button = els.signOutBtn;
    if (!button || button.disabled) return;

    button.disabled = true;
    button.classList.add('is-busy');
    button.setAttribute('title', 'Wylogowywanie…');

    try {
      const { error } = await supabase.auth.signOut({ scope:'local' });
      if (error) throw error;

      // onAuthStateChange powinien obsłużyć SIGNED_OUT, ale wywołanie
      // poniżej gwarantuje natychmiastowe zamknięcie aplikacji również
      // wtedy, gdy zdarzenie Supabase dotrze z opóźnieniem.
      await applySession(null);
    } catch (error) {
      console.error('[D24 AUTH] Wylogowanie nie powiodło się:', error);
      button.disabled = false;
      button.classList.remove('is-busy');
      button.setAttribute('title', 'Wyloguj');
      setBadge(els.connectionBadge, 'Błąd wylogowania', 'error');
      window.alert(`Nie udało się wylogować: ${error.message}`);
    }
  });
  els.newRouteBtn.addEventListener('click', () => {
    newRoute();
    showWorkspace('route');
  });
  els.newRouteNavBtn?.addEventListener('click', () => showWorkspace('route'));
  els.reportsNavBtn?.addEventListener('click', () => {
    showWorkspace('reports');
    refreshLineManager().catch(error => console.warn('[D24 RAPORTY] Odświeżenie:', error));
  });
  els.diagnosticsBtn?.addEventListener('click', () => {
    // diagnostics.js otwiera właściwy widok i pobiera dane; tutaj tylko synchronizujemy pozostałe zakładki.
    els.reportsWorkspace?.classList.add('hidden');
    setTopNavigation('diagnostics');
  });
  document.querySelectorAll('[data-routes-carrier]').forEach(button => {
    button.addEventListener('click', () => {
      setRoutesCarrierFilter(button.dataset.routesCarrier || '');
      document.querySelectorAll('[data-routes-carrier]').forEach(item => item.classList.toggle('active', item === button));
      renderRoutes();
    });
  });
  els.pdfFile.addEventListener('change', importPdf);
  els.resolvePointsBtn.addEventListener('click', resolvePoints);
  els.analyzeRouteBtn.addEventListener('click', analyzeRoute);
  els.saveRouteBtn.addEventListener('click', saveRoute);
  els.runAllCalculatorsBtn?.addEventListener('click', runAllCalculatorsForCurrentLine);
  els.editPointsBtn?.addEventListener('click', () => enterPointsEditMode());
  els.reversePointsBtn.addEventListener('click', reverseEditableRoute);
  els.addPointBtn.addEventListener('click', () => addEditablePoint());
  els.fitMapBtn.addEventListener('click', () => fitRoute(pointsForCurrentView()));
  els.exportReportBtn.addEventListener('click', exportCurrentReport);
  els.openMapPreviewBtn?.addEventListener('click', openMapPreview);
  els.closeMapPreviewBtn?.addEventListener('click', closeMapPreview);
  els.mapPreviewDialog?.addEventListener('click', event => { if (event.target === els.mapPreviewDialog) closeMapPreview(); });
  els.closeBorderEditorBtn?.addEventListener('click', closeBorderEditor);
  els.cancelBorderEditBtn?.addEventListener('click', closeBorderEditor);
  els.pickBorderPointBtn?.addEventListener('click', startBorderPointPick);
  els.saveBorderEditBtn?.addEventListener('click', saveBorderEdit);
  els.borderEditorLat?.addEventListener('change', syncBorderMarkerFromInputs);
  els.borderEditorLon?.addEventListener('change', syncBorderMarkerFromInputs);
  els.closeMytoDialogBtn?.addEventListener('click', closeMytoDialog);
  els.cancelMytoDialogBtn?.addEventListener('click', closeMytoDialog);
  els.mytoDialog?.addEventListener('click', event => { if (event.target === els.mytoDialog) closeMytoDialog(); });
  els.mytoForm?.addEventListener('submit', submitMytoDialog);
  els.refreshMytoBordersBtn?.addEventListener('click', refreshCurrentMytoBorders);
  els.closeAsfinagDialogBtn?.addEventListener('click', closeAsfinagDialog);
  els.cancelAsfinagDialogBtn?.addEventListener('click', closeAsfinagDialog);
  els.asfinagDialog?.addEventListener('click', event => { if (event.target === els.asfinagDialog) closeAsfinagDialog(); });
  els.asfinagForm?.addEventListener('submit', submitAsfinagDialog);
  els.closeHugoDialogBtn?.addEventListener('click', closeHugoDialog);
  els.cancelHugoDialogBtn?.addEventListener('click', closeHugoDialog);
  els.hugoDialog?.addEventListener('click', event => { if (event.target === els.hugoDialog) closeHugoDialog(); });
  els.hugoForm?.addEventListener('submit', submitHugoDialog);

  [els.routeTitle, els.routeLineNumber, els.routeDirection]
    .filter(Boolean)
    .forEach(input => input.addEventListener('input', () => {
      syncHeaderFields();
      updateButtons();
    }));

  [els.routeCarrier, els.routeDirectionType]
    .filter(Boolean)
    .forEach(input => input.addEventListener('change', () => {
      syncHeaderFields();
      updateButtons();
    }));
}



export async function bootstrap() {
  window.addEventListener(RENDER_EVENT, renderAll);
  configureAuth({ startProtectedApp });
  bindEvents();
  try {
    await initialize();
  } catch (error) {
    console.error(error);
    const message = error?.message || String(error);
    if (els.appShell?.classList.contains('hidden')) {
      const { showLoginGate } = await import('./auth.js');
      showLoginGate(message, 'error');
    } else {
      setStatus(els.importStatus, message, 'error');
    }
  }
}
